/**
 * <s-na-stage> — phone home New Arrivals product stage (templates/index.liquid, merchant 2026-11-01).
 * The photos sit in one native scroll-snap row ([data-nas-track], same feel as the product-page gallery): the photo
 * follows the finger and settles, one product per swipe, with the phone's own edge bounce. While it moves, the
 * product-page dots follow live; once it settles, the matching thumbnail radio is checked and that product's 3D panel
 * swaps in with a slow fade (motion rule: calm, 0.6s; none with reduced motion). Tapping a thumbnail glides the row
 * to that photo.
 */
class SNaStage extends HTMLElement {
  #track = null;
  #frame = 0;
  #settle = 0;
  #index = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-nas-track]');
    this.addEventListener('change', this.#onChange);
    this.#track?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#track?.addEventListener('scrollend', this.#onSettle);
    // The first panel opens its 3D on Size once laid out.
    customElements.whenDefined('s-spec-glance').then(() => requestAnimationFrame(() => this.querySelector('[data-nas-panel]:not([hidden]) s-spec-glance')?.showStart?.()));
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#track?.removeEventListener('scrollend', this.#onSettle);
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#settle);
  }

  get #reduced() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #current() {
    const track = this.#track;
    if (!track) return 0;
    const count = track.children.length;
    return Math.min(count - 1, Math.max(0, Math.round(Math.abs(track.scrollLeft) / (track.clientWidth || 1))));
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#setDots(this.#current()));
    // Fallback where `scrollend` is missing (older Safari).
    clearTimeout(this.#settle);
    this.#settle = setTimeout(this.#onSettle, 160);
  };

  #onSettle = () => {
    clearTimeout(this.#settle);
    const index = this.#current();
    if (index === this.#index) return;
    const radio = this.querySelectorAll('input[name="fz-nas"]')[index];
    if (radio) radio.checked = true;
    this.#show(index);
    radio?.closest('.fz-nas__thumb')?.scrollIntoView({ behavior: this.#reduced ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
  };

  // Thumbnail tapped: glide the photo row there; the panel swaps when it settles (or now if already there).
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'fz-nas') return;
    const index = [...this.querySelectorAll('input[name="fz-nas"]')].indexOf(input);
    const slide = this.#track?.children[index];
    if (!slide) return;
    const left = slide.offsetLeft - this.#track.firstElementChild.offsetLeft;
    if (Math.abs(this.#track.scrollLeft - left) < 2) { this.#show(index); return; }
    this.#track.scrollTo({ left, behavior: this.#reduced ? 'auto' : 'smooth' });
  };

  #setDots(index) {
    const dots = this.querySelector('[data-dots]');
    if (dots) Array.from(dots.children).forEach((dot, i) => dot.toggleAttribute('data-on', i === index));
  }

  #show(index) {
    this.#index = index;
    this.#setDots(index);
    this.querySelectorAll('[data-nas-panel]').forEach((panel, i) => {
      const on = i === index;
      if (panel.hidden === !on) return;
      panel.hidden = !on;
      if (!on) return;
      panel.querySelector('s-spec-glance')?.showStart?.();
      if (!this.#reduced) panel.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 600, easing: 'cubic-bezier(.22,.61,.36,1)' });
    });
  }
}

if (!customElements.get('s-na-stage')) customElements.define('s-na-stage', SNaStage);
